import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { AcademicCapIcon, SparklesIcon, EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';

const LoginPage = ({ onLogin }) => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError(''); // Clear error when user types
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.email.trim() || !formData.password.trim()) {
      setError('Please enter both email and password');
      return;
    }

    setIsLoading(true);
    setError('');
    
    try {
      // Call the onLogin function which will handle the authentication
      const result = await onLogin(formData.email, formData.password);
      
      if (result && result.success) {
        // Login successful, user will be redirected by the auth context
        console.log('Login successful');
      }
    } catch (error) {
      console.error('Login error:', error);
      
      // Provide specific error messages
      if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
        setError('Unable to connect to server. Please check your internet connection.');
      } else if (error.message.includes('401') || error.message.includes('Invalid credentials')) {
        setError('Invalid email or password. Please try again.');
      } else if (error.message.includes('403')) {
        setError('Account access denied. Please contact support.');
      } else {
        setError('Login failed. Please try again or contact support.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden bg-gradient-to-br from-primary-50 to-secondary-50">
      {/* Background decorations */}
      <div className="absolute inset-0 z-0">
        <div className="absolute top-20 left-20 w-32 h-32 bg-primary-200 rounded-full opacity-20 animate-bounce-subtle"></div>
        <div className="absolute top-40 right-32 w-24 h-24 bg-secondary-200 rounded-full opacity-20 animate-bounce-subtle" style={{animationDelay: '1s'}}></div>
        <div className="absolute bottom-32 left-1/4 w-20 h-20 bg-accent-200 rounded-full opacity-20 animate-bounce-subtle" style={{animationDelay: '2s'}}></div>
        <div className="absolute bottom-20 right-20 w-28 h-28 bg-primary-300 rounded-full opacity-20 animate-bounce-subtle" style={{animationDelay: '0.5s'}}></div>
      </div>

      <div className="w-full max-w-md z-10 animate-fade-in">

        {/* Login Card */}
        <div className="glass-effect rounded-3xl p-8 shadow-2xl mt-10">
          <div className="flex items-center justify-center mb-6">
            <div className="flex items-center space-x-2 text-primary-700">
              <AcademicCapIcon className="w-6 h-6" />
              <span className="font-semibold text-lg">Student Portal</span>
              <SparklesIcon className="w-5 w-5 text-accent-500" />
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-md p-4">
                <p className="text-red-600 text-sm">{error}</p>
              </div>
            )}

            <div>
              <label htmlFor="email" className="form-label mb-2">
                Email Address
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="input-field"
                placeholder="Enter your email address"
                disabled={isLoading}
              />
            </div>

            <div>
              <label htmlFor="password" className="form-label mb-2">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  className="input-field pr-10"
                  placeholder="Enter your password"
                  disabled={isLoading}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                  ) : (
                    <EyeIcon className="h-5 w-5 text-gray-400" />
                  )}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <input
                  id="remember-me"
                  name="remember-me"
                  type="checkbox"
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-900">
                  Remember me
                </label>
              </div>

              <div className="text-sm">
                <button type="button" className="font-medium text-primary-600 hover:text-primary-500">
                  Forgot password?
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className={`w-full btn-primary ${isLoading ? 'opacity-70 cursor-not-allowed' : ''}`}
            >
              {isLoading ? (
                <div className="flex items-center justify-center">
                  <svg className="animate-spin h-5 w-5 text-white mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span>Signing In...</span>
                </div>
              ) : (
                'Sign In'
              )}
            </button>
          </form>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">New to Aitech?</span>
              </div>
            </div>

            <div className="mt-6">
              <Link
                to="/register"
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-primary-700 bg-primary-100 hover:bg-primary-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors duration-200"
              >
                Create New Account
              </Link>
            </div>
          </div>

          <div className="mt-6 text-center">
            <div className="text-sm text-gray-500 mb-4">
              ✨ Experience personalized AI-powered learning
            </div>
            <div className="flex items-center justify-center space-x-6 text-xs text-gray-400">
              <div className="flex items-center">
                <div className="w-2 h-2 bg-secondary-500 rounded-full mr-2"></div>
                Interactive Content
              </div>
              <div className="flex items-center">
                <div className="w-2 h-2 bg-primary-500 rounded-full mr-2"></div>
                AI Tutoring
              </div>
              <div className="flex items-center">
                <div className="w-2 h-2 bg-accent-500 rounded-full mr-2"></div>
                Progress Tracking
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-8 text-sm text-gray-500">
          Need help? Contact our support team
        </div>
      </div>
    </div>
  );
};

export default LoginPage; 